import type { MappingProfile } from '@automapper/core';
import { mapFrom, mapWithArguments, preCondition } from '@automapper/core';
import { CreateAccountProps } from '@tcs/create-account/components/Steps';
import { PartnerConfigurationType } from '@tcs/create-account/utils/prismic/types';
import { isNil } from 'lodash';
import { Address } from '../types/Address';

export const partnerConfigProfile: MappingProfile = (mapper) => {
  mapper
    .createMap<PartnerConfigurationType, CreateAccountProps>(
      'PartnerConfigurationType',
      'CreateAccountProps'
    )
    .forMember(
      (dest) => dest.hidePromos,
      mapFrom((source) => source.hide_promos)
    )
    .forMember(
      (dest) => dest.hideAccountFeatures,
      mapFrom((source) => source.hide_account_features)
    )
    .forMember(
      (dest) => dest.hideOnlineSetup,
      mapFrom((source) => source.hide_online_setup)
    )
    .forMember(
      (dest) => dest.hideClientInfo,
      mapFrom((source) => source.hide_client_info)
    )
    .forMember(
      (dest) => dest.hideBeneficiaries,
      mapFrom((source) => source.hide_beneficiaries)
    )
    .forMember(
      (dest) => dest.hideAffiliations,
      mapFrom((source) => source.hide_affiliations)
    )
    .forMember(
      (dest) => dest.hideInvestments,
      mapFrom((source) => source.hide_investments)
    )
    .forMember(
      (dest) => dest.hideUSMail,
      mapFrom((source) => source.hide_us_mail)
    )
    .forMember(
      (dest) => dest.hideSaveProgress,
      mapFrom((source) => source.hide_save_progress)
    )
    .forMember(
      (dest) => dest.disableFundAccount,
      mapFrom((source) => source.disable_fund_account)
    )
    .forMember(
      (dest) => dest.hideFundAccountRequests,
      mapFrom((source) => source.hide_fund_account_request)
    )
    .forMember(
      (dest) => dest.defaultPhoneNumber,
      mapFrom((source) => source.default_phone_number)
    )
    .forMember(
      (dest) => dest.promoCode,
      mapFrom((source) => source.promo_code)
    )
    .forMember(
      (dest) => dest.documentsUrl,
      mapFrom((source) => source.documents_url)
    )
    .forMember(
      (dest) => dest.stepsOrder,
      mapFrom((source) => source.steps_order.map((x) => x.step))
    )
    .forMember(
      (dest) => dest.apiKey,
      mapFrom((source) => source.api_key)
    )
    .forMember(
      (dest) => dest.apiSecret,
      mapFrom((source) => source.api_secret)
    )
    .forMember(
      (dest) => dest.preSavedViewModel.representativeInformation,
      mapWithArguments((source, { representativeCode }) => {
        if (representativeCode) {
          return;
        }
        return source.representative_information_enabled ?? false;
      })
    )
    .forMember(
      (dest) => dest.preSavedViewModel.representativeFirstName,
      mapFrom((source) => source.representative_firstname)
    )
    .forMember(
      (dest) => dest.preSavedViewModel.representativeLastName,
      mapFrom((source) => source.representative_lastname)
    )
    .forMember(
      (dest) => dest.preSavedViewModel.representativePhoneCountryCode,
      mapFrom((source) => source.representative_phone_country_code)
    )
    .forMember(
      (dest) => dest.preSavedViewModel.representativePhone,
      mapFrom((source) => source.representative_phone_number)
    )
    .forMember(
      (dest) => dest.preSavedViewModel.representativeEmail,
      mapFrom((source) => source.representative_email)
    )
    .forMember(
      (dest) => dest.preSavedViewModel.representativeCompanyName,
      mapFrom((source) => source.representative_company_name)
    )
    .forMember(
      (dest) => dest.preSavedViewModel.representativeCompanyPhoneCountryCode,
      mapFrom((source) => source.representative_company_phone_country_code)
    )
    .forMember(
      (dest) => dest.preSavedViewModel.representativeCompanyPhone,
      mapFrom((source) => source.representative_company_phone_number)
    )
    .forMember(
      (dest) => dest.preSavedViewModel.representativeAddress,
      preCondition((source) => !isNil(source.representative_address_line_1)),
      mapFrom((source) =>
        mapper.map<PartnerConfigurationType, Address>(
          source,
          'Address',
          'PartnerConfigurationType'
        )
      )
    );
};
